import React, { useRef } from 'react'
import { DateRangePickerDay, DateRangePickerDayProps } from '@mui/lab'
import { DateRange } from '@mui/lab/DateRangePicker'

/**
 * The desktop range picker of @mui/lab 5.0.0-alpha answers a dismissal (focus leaving the inputs, a click
 * outside the calendar, Escape) with onChange(<range last accepted from the calendar>) followed in the same
 * call by onClose, which throws away any date typed into the inputs since then (KWA-29163).
 *
 * Typed changes carry the input text and calendar picks are flagged through renderDay, so either is applied
 * at once. Any other change is held until the current call stack finishes and dropped if the picker closed
 * in the meantime: the inputs then keep what the user can see. Mobile is left alone, where a dismissal is
 * the dialog's Cancel and reverting is what it means.
 */
const useKeepTypedDateRange = (onChange: (value: DateRange<Date>) => void, enabled: boolean) => {
  const isCalendarSelectionRef = useRef(false)
  const pendingChangeRef = useRef<{ value: DateRange<Date> } | null>(null)

  const handleChange = (value: DateRange<Date>, keyboardInputValue?: string) => {
    if (!enabled || keyboardInputValue !== undefined || isCalendarSelectionRef.current) {
      onChange(value)
      return
    }
    const pendingChange = { value }
    pendingChangeRef.current = pendingChange
    Promise.resolve().then(() => {
      if (pendingChangeRef.current !== pendingChange) return
      pendingChangeRef.current = null
      onChange(value)
    })
  }

  const handleClose = () => {
    pendingChangeRef.current = null
  }

  const renderDay = (_day: Date, dayProps: DateRangePickerDayProps<Date>) => (
    <DateRangePickerDay
      {...dayProps}
      onDaySelect={(day, isFinish) => {
        isCalendarSelectionRef.current = true
        try {
          dayProps.onDaySelect(day, isFinish)
        } finally {
          isCalendarSelectionRef.current = false
        }
      }}
    />
  )

  return { handleChange, handleClose, renderDay }
}

export default useKeepTypedDateRange
